import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../../../../api/client'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { CHECK_STATES, SYSTEM_VERIFICATION_COPY } from '../constants/systemVerificationContent'

const DEFAULT_PORT_RANGE = {
  start: 4000,
  end: 4010,
}
const FALLBACK_MIN_AGENT_VERSION = '2.1.1'
const PERMISSION_GRACE_MS = 12000
const PERMISSION_POLL_INTERVAL_MS = 700
const PROBE_TIMEOUT_MS = 1400

function resolveUserId(user) {
  return user?.user_id || user?.userId || user?.id || null
}

function resolveToken(result) {
  return result?.token || result?.access_token || null
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 1600) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timeout)
  }
}

function parseVersion(versionValue) {
  if (!versionValue) return [0, 0, 0]
  const clean = String(versionValue).trim().replace(/^v/i, '')
  const [major = '0', minor = '0', patch = '0'] = clean.split('.')
  return [major, minor, patch].map((segment) => Number.parseInt(segment, 10) || 0)
}

function isVersionAtLeast(current, minimum) {
  const currentParts = parseVersion(current)
  const minimumParts = parseVersion(minimum)
  for (let index = 0; index < 3; index += 1) {
    if (currentParts[index] > minimumParts[index]) return true
    if (currentParts[index] < minimumParts[index]) return false
  }
  return true
}

function getChromeMajorVersion() {
  if (typeof navigator === 'undefined') return 0
  const match = String(navigator.userAgent || '').match(/Chrome\/(\d+)/)
  return match ? Number.parseInt(match[1], 10) || 0 : 0
}

function detectClientPlatform() {
  const ua = String(navigator?.userAgent || '').toLowerCase()
  if (ua.includes('win')) return 'win32'
  if (ua.includes('mac')) return 'darwin'
  return 'other'
}

export function useSystemVerification() {
  const navigate = useNavigate()
  const location = useLocation()
  const loginContext = location.state || {}
  const { nonce, user, agentPortRange, rememberMe, sessionResolutionAction } = loginContext
  const userId = resolveUserId(user)

  // ── 4 Distinct Verification States ─────────────────────────────────────────
  const [browserAccess, setBrowserAccess] = useState(CHECK_STATES.PENDING)
  const [agentRunning, setAgentRunning] = useState(CHECK_STATES.PENDING)
  const [versionCheck, setVersionCheck] = useState(CHECK_STATES.PENDING)
  const [osPermissions, setOsPermissions] = useState(CHECK_STATES.PENDING)

  const [agentInfo, setAgentInfo] = useState(null)
  const [downloadOptions, setDownloadOptions] = useState([
    { label: 'Windows', icon: 'desktop_windows', href: '/api/activity-monitor/agent-download-file/windows' },
    { label: 'macOS', icon: 'laptop_mac', href: '/api/activity-monitor/agent-download-file/mac' },
  ])
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState('')
  const [hasStartedScan, setHasStartedScan] = useState(false)
  const [permissionState, setPermissionState] = useState('idle')
  const [isAwaitingPermissionDecision, setIsAwaitingPermissionDecision] = useState(false)

  // ── Failure Classification for Contextual Troubleshooting Guidance ─────────
  // 'none' | 'browser_blocked' | 'agent_not_running' | 'version_outdated' | 'mac_accessibility'
  const [failureCause, setFailureCause] = useState('none')

  // ── Version Policy (fetched from BossGlobalERP.AgentReleasePolicy) ─────────
  const [versionPolicy, setVersionPolicy] = useState({
    latestVersion: FALLBACK_MIN_AGENT_VERSION,
    minSupportedVersion: FALLBACK_MIN_AGENT_VERSION,
    forceUpdate: true,
    releaseNotes: '',
    windowsDownloadUrl: '/api/activity-monitor/agent-download-file/windows',
    macDownloadUrl: '/api/activity-monitor/agent-download-file/mac',
  })
  // 'UP_TO_DATE' | 'UPDATE_AVAILABLE' | 'OUTDATED_BLOCKED'
  const [versionStatus, setVersionStatus] = useState('UP_TO_DATE')
  const [installedVersion, setInstalledVersion] = useState(null)

  const clientPlatform = detectClientPlatform()

  const isScanning =
    browserAccess === CHECK_STATES.CHECKING ||
    agentRunning === CHECK_STATES.CHECKING ||
    versionCheck === CHECK_STATES.CHECKING ||
    osPermissions === CHECK_STATES.CHECKING

  const isReadyToContinue =
    browserAccess === CHECK_STATES.PASS &&
    agentRunning === CHECK_STATES.PASS &&
    versionCheck === CHECK_STATES.PASS &&
    osPermissions === CHECK_STATES.PASS &&
    !isVerifying &&
    !isAwaitingPermissionDecision

  const statusMap = useMemo(
    () => ({
      browserAccess,
      agentRunning,
      versionCheck,
      osPermissions,
    }),
    [browserAccess, agentRunning, versionCheck, osPermissions]
  )

  const setAllChecking = useCallback(() => {
    setBrowserAccess(CHECK_STATES.CHECKING)
    setAgentRunning(CHECK_STATES.CHECKING)
    setVersionCheck(CHECK_STATES.CHECKING)
    setOsPermissions(CHECK_STATES.CHECKING)
    setError('')
    setFailureCause('none')
  }, [])

  // ── Fetch active version policy on mount ───────────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function loadVersionPolicy() {
      try {
        const response = await api.get('/activity-monitor/agent-version-policy')
        const data = response.data
        if (!data || cancelled) return
        setVersionPolicy({
          latestVersion: data.latestVersion || FALLBACK_MIN_AGENT_VERSION,
          minSupportedVersion: data.minSupportedVersion || FALLBACK_MIN_AGENT_VERSION,
          forceUpdate: Boolean(data.forceUpdate),
          releaseNotes: data.releaseNotes || '',
          windowsDownloadUrl: data.windowsDownloadUrl || '/api/activity-monitor/agent-download-file/windows',
          macDownloadUrl: data.macDownloadUrl || '/api/activity-monitor/agent-download-file/mac',
        })
        const isWindows = detectClientPlatform() === 'win32'
        const winOption = { label: 'Windows (.exe)', icon: 'desktop_windows', href: data.windowsDownloadUrl || '/api/activity-monitor/agent-download-file/windows' }
        const macOption = { label: 'macOS (.dmg)', icon: 'laptop_mac', href: data.macDownloadUrl || '/api/activity-monitor/agent-download-file/mac' }
        setDownloadOptions(isWindows ? [winOption, macOption] : [macOption, winOption])
      } catch {
        // Safe default fallback already set
      }
    }
    loadVersionPolicy()
    return () => { cancelled = true }
  }, [])

  // ── Query Chrome Private Network Access Permission ─────────────────────────
  const queryLocalNetworkPermission = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return { state: 'unsupported', status: null }
    }
    const chromeMajorVersion = getChromeMajorVersion()
    if (chromeMajorVersion > 0 && chromeMajorVersion < 137) {
      return { state: 'unsupported', status: null }
    }
    try {
      const status = await navigator.permissions.query({ name: 'local-network-access' })
      const state = String(status?.state || '').toLowerCase()
      if (state === 'granted' || state === 'denied' || state === 'prompt') {
        return { state, status }
      }
      return { state: 'unsupported', status: null }
    } catch {
      return { state: 'unsupported', status: null }
    }
  }, [])

  const triggerPermissionProbe = useCallback(async () => {
    const probePort = agentPortRange?.start || DEFAULT_PORT_RANGE.start
    try {
      await fetchWithTimeout(`http://localhost:${probePort}/status`, {}, PROBE_TIMEOUT_MS)
      return true
    } catch {
      return false
    }
  }, [agentPortRange])

  // ── Core 4-Point Inspection Scan ───────────────────────────────────────────
  const runVerificationScan = useCallback(async () => {
    setAllChecking()
    setAgentInfo(null)
    setIsAwaitingPermissionDecision(true)

    const start = agentPortRange?.start || DEFAULT_PORT_RANGE.start
    const end = agentPortRange?.end || DEFAULT_PORT_RANGE.end
    let networkBlocked = false
    let foundAgent = null

    // 1. Probe ports (dual probe 127.0.0.1 and localhost)
    for (let port = start; port <= end; port += 1) {
      try {
        let response = null
        let resolvedHost = '127.0.0.1'
        try {
          response = await fetchWithTimeout(`http://127.0.0.1:${port}/status`)
          resolvedHost = '127.0.0.1'
        } catch {
          response = await fetchWithTimeout(`http://localhost:${port}/status`)
          resolvedHost = 'localhost'
        }
        if (!response || !response.ok) continue
        const data = await response.json()
        if (data?.status === 'running') {
          foundAgent = { ...data, port, host: resolvedHost }
          break
        }
      } catch (scanError) {
        const msg = String(scanError?.message || '').toLowerCase()
        if (scanError?.name === 'TypeError' || msg.includes('failed to fetch')) {
          networkBlocked = true
        }
      }
    }

    setIsAwaitingPermissionDecision(false)

    // Check Chrome Private Network Access Permission state
    const pnaPermission = await queryLocalNetworkPermission()

    // Check 1: Browser Network Access Evaluation
    if (foundAgent) {
      setBrowserAccess(CHECK_STATES.PASS)
      setAgentRunning(CHECK_STATES.PASS)
      setAgentInfo(foundAgent)
    } else {
      // Agent was not detected on any port.
      // If browser explicitly denied permission, report browser blocked.
      if (pnaPermission.state === 'denied') {
        setBrowserAccess(CHECK_STATES.FAIL)
        setAgentRunning(CHECK_STATES.PENDING)
        setVersionCheck(CHECK_STATES.PENDING)
        setOsPermissions(CHECK_STATES.PENDING)
        setFailureCause('browser_blocked')
        setError(SYSTEM_VERIFICATION_COPY.appsPermissionGuidance)
        return { ok: false, cause: 'browser_blocked' }
      }

      // If browser permission is granted, prompt, or unsupported:
      // Browser network access is OK — the desktop agent daemon is simply not running!
      setBrowserAccess(CHECK_STATES.PASS)
      setAgentRunning(CHECK_STATES.FAIL)
      setVersionCheck(CHECK_STATES.PENDING)
      setOsPermissions(CHECK_STATES.PENDING)
      setFailureCause('agent_not_running')
      setError(SYSTEM_VERIFICATION_COPY.agentNotRunningGuidance)
      return { ok: false, cause: 'agent_not_running' }
    }

    // Check 3: Version Compatibility Evaluation
    const agentVersion = String(foundAgent?.version || '0.0.0')
    setInstalledVersion(agentVersion)
    const effectiveMin = versionPolicy.forceUpdate
      ? versionPolicy.latestVersion
      : versionPolicy.minSupportedVersion

    if (!isVersionAtLeast(agentVersion, effectiveMin)) {
      setVersionCheck(CHECK_STATES.FAIL)
      setOsPermissions(CHECK_STATES.PENDING)
      setVersionStatus('OUTDATED_BLOCKED')
      setFailureCause('version_outdated')
      setError(
        `Bridge Agent v${agentVersion} is outdated.\n` +
        `Version ${effectiveMin} or higher is required to support ambient domain monitoring.\n` +
        `Please download and run the latest installer below.`
      )
      return { ok: false, cause: 'version_outdated' }
    }

    // Version satisfies minimum requirement
    setVersionCheck(CHECK_STATES.PASS)
    if (!isVersionAtLeast(agentVersion, versionPolicy.latestVersion)) {
      setVersionStatus('UPDATE_AVAILABLE')
    } else {
      setVersionStatus('UP_TO_DATE')
    }

    // Check 4: OS-Level Permissions Evaluation
    const platform = String(foundAgent?.platform || '').toLowerCase()
    if (platform === 'darwin') {
      const hasAccessibility = Boolean(foundAgent?.permissions?.accessibility)
      if (!hasAccessibility) {
        setOsPermissions(CHECK_STATES.FAIL)
        setFailureCause('mac_accessibility')
        setError(SYSTEM_VERIFICATION_COPY.macAccessibilityGuidance)
        return { ok: false, cause: 'mac_accessibility' }
      }
    }

    // Windows or macOS with accessibility granted
    setOsPermissions(CHECK_STATES.PASS)
    setFailureCause('none')
    setError('')
    return { ok: true, agentData: foundAgent }
  }, [agentPortRange, setAllChecking, versionPolicy])

  const startVerificationScan = useCallback(async () => {
    setHasStartedScan(true)
    await runVerificationScan()
  }, [runVerificationScan])

  const retryVerification = useCallback(async () => {
    await runVerificationScan()
  }, [runVerificationScan])

  // ── Step 2 Verification Submission (Issues JWT & Starts Tracking) ──────────
  const completeVerification = useCallback(async () => {
    if (!nonce || !userId || !agentInfo) {
      setError('Verification context expired. Please login again.')
      return
    }

    if (!isReadyToContinue) {
      setError('Please complete all verification checks before continuing.')
      return
    }

    setIsVerifying(true)
    setError('')

    try {
      const agentHost = agentInfo?.host || '127.0.0.1'
      const agentResponse = await fetchWithTimeout(
        `http://${agentHost}:${agentInfo.port}/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonce }),
        },
        3000
      )

      if (!agentResponse.ok) {
        throw new Error('Unable to validate signature from local agent.')
      }

      const agentData = await agentResponse.json()

      const backendResponse = await api.post('/activity-monitor/verify-agent', {
        userId,
        nonce,
        signature: agentData?.signature,
        deviceId: agentData?.deviceId,
        agentPort: agentInfo.port,
        agentVersion: agentInfo?.version || null,
        platform: agentInfo?.platform || clientPlatform || null,
        rememberMe: Boolean(rememberMe),
        sessionResolutionAction: sessionResolutionAction || undefined,
      })

      const result = backendResponse.data
      const token = resolveToken(result)

      if (!result?.success || !token) {
        throw new Error(result?.detail || 'Backend verification failed.')
      }

      const resolvedBackendUrl = String(
        api?.defaults?.baseURL ||
        import.meta.env.VITE_API_URL ||
        'http://127.0.0.1:8000'
      ).trim().replace(/\/+$/, '')

      const trackingResponse = await fetchWithTimeout(
        `http://${agentHost}:${agentInfo.port}/start-tracking`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            sessionId: result?.sessionId,
            token,
            backendUrl: resolvedBackendUrl,
            idleWarningMinutes: result?.idleWarningMinutes,
            idleLogoutMinutes: result?.idleLogoutMinutes,
            activeCheckModalTimeoutMinutes: result?.activeCheckModalTimeoutMinutes ?? 0,
          }),
        },
        3000
      )

      if (!trackingResponse.ok) {
        let detail = ''
        try {
          const payload = await trackingResponse.json()
          detail = String(payload?.error || payload?.detail || '').trim()
        } catch {
          try {
            detail = String(await trackingResponse.text()).trim()
          } catch {
            detail = ''
          }
        }
        throw new Error(
          `Local agent verification passed, but tracking failed to start${detail ? `: ${detail}` : '.'}`
        )
      }

      localStorage.setItem('authToken', token)
      localStorage.setItem('user', JSON.stringify(result?.user || user))
      sessionStorage.setItem('agentPort', String(agentInfo.port))
      if (result?.sessionId) {
        sessionStorage.setItem('sessionId', result.sessionId)
      }

      navigate(result?.redirectTo || '/dashboard/personal', { replace: true })
    } catch (verificationError) {
      const message = extractApiErrorMessage(
        verificationError,
        'System verification failed. Please retry.'
      )
      const loweredMessage = String(message || '').toLowerCase()
      const apiCode = verificationError?.response?.data?.detail?.code
      if (
        apiCode === 'HTTP_400' &&
        loweredMessage.includes('invalid or expired verification challenge')
      ) {
        localStorage.removeItem('authToken')
        sessionStorage.removeItem('agentPort')
        sessionStorage.removeItem('sessionId')
        navigate('/login', {
          replace: true,
          state: {
            message: 'System verification challenge expired. Please login again.',
          },
        })
        return
      }
      setError(message)
    } finally {
      setIsVerifying(false)
    }
  }, [agentInfo, clientPlatform, isReadyToContinue, navigate, nonce, rememberMe, sessionResolutionAction, user, userId])

  return {
    statusMap,
    platform: agentInfo?.platform || clientPlatform || null,
    hasStartedScan,
    isAwaitingPermissionDecision,
    isScanning,
    isVerifying,
    isReadyToContinue,
    error,
    failureCause,
    downloadOptions,
    versionPolicy,
    versionStatus,
    installedVersion,
    startVerificationScan,
    retryVerification,
    completeVerification,
    goBackToLogin: () => navigate('/login', { replace: true }),
  }
}
